import { Underline } from "lucide-react";
import React from "react";
import {
  CenterUnderline,
  ComesInGoesOutUnderline,
  GoesOutComesInUnderline,
} from "@/components/ui/underline-animation";

// Komponent podglądu
export const UnderlineAnimationPreview = () => (
  <div className="flex flex-col items-center justify-center gap-8 py-8">
    <div className="flex items-center gap-6">
      <CenterUnderline label="Center Underline" />
    </div>

    <div className="flex items-center gap-6">
      <ComesInGoesOutUnderline
        label="Comes In Goes Out (Left)"
        direction="left"
      />
      <ComesInGoesOutUnderline
        label="Comes In Goes Out (Right)"
        direction="right"
      />
    </div>

    <div className="flex items-center gap-6">
      <GoesOutComesInUnderline
        label="Goes Out Comes In (Left)"
        direction="left"
      />
      <GoesOutComesInUnderline
        label="Goes Out Comes In (Right)"
        direction="right"
      />
    </div>
  </div>
);

const component = {
  id: "underline-animation",
  title: "Underline Animation",
  component: CenterUnderline,
  description:
    "A collection of text underline animations for interactive elements.",
  preview: UnderlineAnimationPreview,
  icon: <Underline className="h-4 w-4 mr-2" />,
  code: `import { CenterUnderline } from "@/components/ui/underline-animation";

export const UnderlineAnimationExample = () => {
  return <CenterUnderline label="Hover me!" />;
};`,
  installation: "This component is already included in the project.",
  detailedInstallation: `
### Installation Steps

1. This component is part of the core UI library in the project.
2. If you're using this in a new project, copy the following files:
   - \`src/components/ui/underline-animation.jsx\`
   - Make sure you have \`framer-motion\` installed in your dependencies

### Dependencies

\`\`\`json
{
  "dependencies": {
    "framer-motion": "^10.12.16",
    "react": "^18.2.0",
    "react-dom": "^18.2.0"
  }
}
\`\`\`
`,
  sourceCode: `import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { cn } from "@/lib/utils";

// Center Underline
export function CenterUnderline({
  label,
  className,
  onClick,
  transition = { duration: 0.25, ease: "easeInOut" },
  underlineHeightRatio = 0.1,
  underlinePaddingRatio = 0.01,
  ...props
}) {
  const textRef = useRef(null);

  useEffect(() => {
    const updateUnderlineStyles = () => {
      if (textRef.current) {
        const fontSize = parseFloat(getComputedStyle(textRef.current).fontSize);
        const underlineHeight = fontSize * underlineHeightRatio;
        const underlinePadding = fontSize * underlinePaddingRatio;
        textRef.current.style.setProperty(
          "--underline-height",
          \`\${underlineHeight}px\`
        );
        textRef.current.style.setProperty(
          "--underline-padding",
          \`\${underlinePadding}px\`
        );
      }
    };

    updateUnderlineStyles();
    window.addEventListener("resize", updateUnderlineStyles);

    return () => window.removeEventListener("resize", updateUnderlineStyles);
  }, [underlineHeightRatio, underlinePaddingRatio]);

  return (
    <motion.span
      className={cn("relative inline-block cursor-pointer", className)}
      whileHover="visible"
      onClick={onClick}
      ref={textRef}
      {...props}
    >
      <span>{label}</span>
      <motion.div
        className="absolute left-1/2 bg-current -translate-x-1/2"
        style={{
          height: "var(--underline-height)",
          bottom: "calc(-1 * var(--underline-padding))",
        }}
        variants={{
          hidden: {
            width: 0,
            originX: 0.5,
          },
          visible: {
            width: "100%",
            transition: transition,
          },
        }}
      />
    </motion.span>
  );
}

// Comes In Goes Out Underline
export function ComesInGoesOutUnderline({
  label,
  direction = "left",
  className,
  onClick,
  underlineHeightRatio = 0.1,
  underlinePaddingRatio = 0.01,
  transition = { duration: 0.4, ease: "easeInOut" },
  ...props
}) {
  const controls = useAnimationControls();
  const [blocked, setBlocked] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const updateUnderlineStyles = () => {
      if (textRef.current) {
        const fontSize = parseFloat(getComputedStyle(textRef.current).fontSize);
        const underlineHeight = fontSize * underlineHeightRatio;
        const underlinePadding = fontSize * underlinePaddingRatio;
        textRef.current.style.setProperty(
          "--underline-height",
          \`\${underlineHeight}px\`
        );
        textRef.current.style.setProperty(
          "--underline-padding",
          \`\${underlinePadding}px\`
        );
      }
    };

    updateUnderlineStyles();
    window.addEventListener("resize", updateUnderlineStyles);

    return () => window.removeEventListener("resize", updateUnderlineStyles);
  }, [underlineHeightRatio, underlinePaddingRatio]);

  const animate = async () => {
    if (blocked) return;

    setBlocked(true);

    await controls.start({
      width: "100%",
      transition,
      transitionEnd: {
        left: direction === "left" ? "auto" : 0,
        right: direction === "left" ? 0 : "auto",
      },
    });

    await controls.start({
      width: 0,
      transition,
      transitionEnd: {
        left: direction === "left" ? 0 : "",
        right: direction === "left" ? "" : 0,
      },
    });

    setBlocked(false);
  };

  return (
    <motion.span
      className={cn("relative inline-block cursor-pointer", className)}
      onHoverStart={animate}
      onClick={onClick}
      ref={textRef}
      {...props}
    >
      <span>{label}</span>
      <motion.span
        className={cn("absolute bg-current w-0", {
          "left-0": direction === "left",
          "right-0": direction === "right",
        })}
        style={{
          height: "var(--underline-height)",
          bottom: "calc(-1 * var(--underline-padding))",
        }}
        animate={controls}
      />
    </motion.span>
  );
}

// Goes Out Comes In Underline
export function GoesOutComesInUnderline({
  label,
  direction = "left",
  className,
  onClick,
  underlineHeightRatio = 0.1,
  underlinePaddingRatio = 0.01,
  transition = { duration: 0.5, ease: "easeOut" },
  ...props
}) {
  const controls = useAnimationControls();
  const [blocked, setBlocked] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const updateUnderlineStyles = () => {
      if (textRef.current) {
        const fontSize = parseFloat(getComputedStyle(textRef.current).fontSize);
        const underlineHeight = fontSize * underlineHeightRatio;
        const underlinePadding = fontSize * underlinePaddingRatio;
        textRef.current.style.setProperty(
          "--underline-height",
          \`\${underlineHeight}px\`
        );
        textRef.current.style.setProperty(
          "--underline-padding",
          \`\${underlinePadding}px\`
        );
      }
    };

    updateUnderlineStyles();
    window.addEventListener("resize", updateUnderlineStyles);

    return () => window.removeEventListener("resize", updateUnderlineStyles);
  }, [underlineHeightRatio, underlinePaddingRatio]);

  const animate = async () => {
    if (blocked) return;

    setBlocked(true);

    await controls.start({
      width: 0,
      transition,
      transitionEnd: {
        left: direction === "left" ? "auto" : 0,
        right: direction === "left" ? 0 : "auto",
      },
    });

    await controls.start({
      width: "100%",
      transition,
      transitionEnd: {
        left: direction === "left" ? 0 : "",
        right: direction === "left" ? "" : 0,
      },
    });

    setBlocked(false);
  };

  return (
    <motion.span
      className={cn("relative inline-block cursor-pointer", className)}
      onHoverStart={animate}
      onClick={onClick}
      ref={textRef}
      {...props}
    >
      <span className="sr-only">{label}</span>
      <span aria-hidden="true">{label}</span>
      <motion.span
        className={cn("absolute bg-current", {
          "left-0": direction === "left",
          "right-0": direction === "right",
        })}
        style={{
          height: "var(--underline-height)",
          bottom: "calc(-1 * var(--underline-padding))",
          width: "100%",
        }}
        animate={controls}
        aria-hidden="true"
      />
    </motion.span>
  );
}`,
  props: [
    {
      name: "label",
      type: "string",
      default: "",
      description: "The text to display with the underline animation.",
    },
    {
      name: "className",
      type: "string",
      default: "''",
      description: "Additional CSS classes to apply to the component.",
    },
    {
      name: "onClick",
      type: "function",
      default: "undefined",
      description: "Function to call when the component is clicked.",
    },
    {
      name: "transition",
      type: "object",
      default: "{ duration: 0.25, ease: 'easeInOut' }",
      description: "Framer Motion transition properties for the animation.",
    },
    {
      name: "underlineHeightRatio",
      type: "number",
      default: "0.1",
      description: "Ratio of the font size to use for the underline height.",
    },
    {
      name: "underlinePaddingRatio",
      type: "number",
      default: "0.01",
      description:
        "Ratio of the font size to use for the space between text and underline.",
    },
    {
      name: "direction",
      type: "string",
      default: "'left'",
      description:
        "Direction of the animation for directional variants (left or right).",
    },
  ],
  examples: [
    {
      title: "Center Underline",
      code: `<CenterUnderline label="Hover me!" />`,
    },
    {
      title: "Comes In Goes Out Underline",
      code: `<ComesInGoesOutUnderline 
  label="Hover me!" 
  direction="left" 
/>`,
    },
    {
      title: "Goes Out Comes In Underline",
      code: `<GoesOutComesInUnderline 
  label="Hover me!" 
  direction="right" 
/>`,
    },
    {
      title: "With click handler",
      code: `<CenterUnderline 
  label="Click me!" 
  onClick={() => alert('Clicked!')} 
/>`,
    },
    {
      title: "Custom styling",
      code: `<CenterUnderline 
  label="Custom Style" 
  className="text-blue-500 text-xl font-bold" 
  underlineHeightRatio={0.15}
/>`,
    },
  ],
};

export default component;
